/**
 * The new job page's words and stops (threads/job-page-fresh, Corbin 10/28).
 * Pure functions over what the page already reads:
 *   GET /api/jobs/:id              the job, its devices, tests, repairs, invoices
 *   GET /api/jobs/:id/trail        city online / filed, packet sent, card, the top box's words
 *   GET /close-out/queue?jobId=    Closeout's own "whose turn" (closeoutBall.ts)
 * Nothing here decides a fact anew: a device's state is the job page's device
 * rule (calm/calmModel.ts classifyDevice), whose turn in Closeout is
 * Closeout's.
 */
import type { Ball, BallTopic } from "@/lib/closeout-ball";
import type { ItemState, OutcomeSet } from "@/lib/job-outcomes";
import type { CalmDevice } from "../detail/calm/calmModel";
import type { JobDetail, LinkedInvoice } from "../detail/types";

export type Phase = "before" | "working" | "closeout" | "after" | "cancelled";

const BEFORE = new Set([
    "draft",
    "awaiting_scheduling",
    "unqueued",
    "scheduled",
    "en_route",
    "dispatched",
    "confirmed",
]);
const WORKING = new Set(["arrived", "in_progress"]);
const CLOSEOUT = new Set(["office_review", "cannot_complete", "held", "ready_for_invoice", "pending_action"]);

export function phaseOf(status: string | null | undefined): Phase {
    const s = (status ?? "").toLowerCase();
    if (s === "cancelled") return "cancelled";
    if (WORKING.has(s)) return "working";
    if (CLOSEOUT.has(s)) return "closeout";
    if (BEFORE.has(s) || !s) return "before";
    return "after";
}

/** One device's state for the top box, from the job page's own device rule. */
export function itemStateOf(d: CalmDevice): ItemState {
    const action = (d.asset.required_action || "test").toLowerCase();
    if (d.word === "Couldn't test" || d.word === "Not at this site") return "couldnt";
    switch (d.tally) {
        case "passed":
            if (action === "repair") return "repaired";
            if (action === "replace") return "replaced";
            return "passed";
        case "failed":
            // A test after a repair failed: the work is still open.
            return action === "test" ? "failed" : "open";
        case "incomplete":
            return "unfinished";
        case "repaired":
            return "repaired";
        case "replaced":
            return "replaced";
        default:
            return d.word === "Repair pending" ? "open" : "todo";
    }
}

/** "Did it pass?" on a pass/fail job; "How did it go?" on everything else. */
export function firstQuestion(set: OutcomeSet, phase: Phase = "closeout"): string {
    if (phase === "before") return "What's on this job?";
    return set.outcomes.some((o) => o.key === "passed" || o.key === "failed") ? "Did it pass?" : "How did it go?";
}

// ── Whose turn ──────────────────────────────────────────────────────────────

export interface Turn {
    tone: "mine" | "waiting" | "done" | "stop";
    text: string;
    sub: string | null;
    action: { label: string; to: string } | null;
}

const STATION: Partial<Record<BallTopic, number>> = {
    reports: 1,
    filing: 1,
    work_order: 1,
    invoice: 2,
    quote: 3,
    payment: 4,
    packet: 5,
};

const ACTION_WORDS: Partial<Record<BallTopic, string>> = {
    reports: "Open the reports",
    filing: "Open city filing",
    work_order: "Add the work order",
    invoice: "Open the invoice",
    quote: "Open the quote",
    payment: "Take the payment",
    packet: "Send to the customer",
};

/** Where the ball's button goes: the Closeout step that does it. */
export function closeoutLink(jobId: string, topic: BallTopic): string {
    const station = STATION[topic];
    return `/close-out?job=${encodeURIComponent(jobId)}${station ? `&station=${station}` : ""}`;
}

const firstName = (n: string | null | undefined) => (n ?? "").trim().split(/\s+/)[0] || "";

export function whoseTurn(input: {
    job: Pick<
        JobDetail,
        | "id"
        | "status"
        | "scheduled_start"
        | "assigned_to_name"
        | "customer_service_hold"
        | "customer_service_hold_reason"
    >;
    phase: Phase;
    ball: Ball | null;
    /** Loaded the queue and the job isn't in it. */
    leftCloseout: boolean;
    paid: boolean;
}): Turn {
    const { job, phase, ball } = input;
    const tech = firstName(job.assigned_to_name);
    if (job.customer_service_hold && phase !== "after") {
        return {
            tone: "stop",
            text: "Do not service — this customer is on hold",
            sub: job.customer_service_hold_reason?.trim() || "No reason was written down.",
            action: null,
        };
    }
    if (phase === "cancelled") return { tone: "done", text: "Cancelled", sub: null, action: null };
    if (phase === "before") {
        if (!job.scheduled_start) {
            return { tone: "mine", text: "Yours — put it on the schedule", sub: "Nobody is booked yet.", action: null };
        }
        if (!tech) return { tone: "mine", text: "Yours — pick a tech", sub: "It has a day but no tech.", action: null };
        return {
            tone: "waiting",
            text: `Ready for ${tech}`,
            sub: "Booked. Nothing to do before the visit.",
            action: null,
        };
    }
    if (phase === "working") {
        return {
            tone: "waiting",
            text: `With ${tech || "the tech"} now`,
            sub: "The visit is happening.",
            action: null,
        };
    }
    if (phase === "closeout" && ball) {
        const tone: Turn["tone"] = ball.mine ? "mine" : "waiting";
        const sub = ball.waits.length > 0 ? `Also: ${ball.waits.join(" · ")}` : (ball.note ?? null);
        return {
            tone,
            text: ball.text,
            sub,
            action: ball.mine
                ? { label: ACTION_WORDS[ball.topic] ?? "Open in Closeout", to: closeoutLink(job.id, ball.topic) }
                : null,
        };
    }
    if (phase === "closeout" && !input.leftCloseout) {
        return { tone: "waiting", text: "In Closeout", sub: null, action: null };
    }
    if (!input.paid) return { tone: "waiting", text: "Waiting on the customer to pay", sub: null, action: null };
    return { tone: "done", text: "Done — nothing left", sub: null, action: null };
}

// ── The trail ───────────────────────────────────────────────────────────────

export interface TrailFacts {
    status: string;
    city: { name: string | null; online: boolean; expected: number; filed: number };
    customer: { lastSentAt: string | null; packetHeld: boolean };
    payment: { netTerms: boolean; cardOnFile: boolean };
}

export type StopState = "done" | "mine" | "waiting" | "later";

export interface Stop {
    key: "visit" | "completed" | "city" | "customer" | "paid";
    name: string;
    state: StopState;
    words: string;
    action: { label: string; to: string } | null;
}

const money$ = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDay = (iso: string | null | undefined) =>
    iso
        ? new Date(iso).toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              timeZone: "America/Chicago",
          })
        : null;

export interface MoneyPicture {
    /** No invoice on the job yet (void ones ignored). */
    none: boolean;
    draft: boolean;
    paid: boolean;
    paidAt: string | null;
    owed: number;
}

/** What the invoices on the job add up to, in the trail's terms. */
export function moneyOf(invoices: LinkedInvoice[]): MoneyPicture {
    const live = invoices.filter((i) => !["void", "cancelled"].includes((i.status ?? "").toLowerCase()));
    if (live.length === 0) return { none: true, draft: false, paid: false, paidAt: null, owed: 0 };
    let owed = 0;
    let paidAt: string | null = null;
    for (const i of live) {
        const status = (i.status ?? "").toLowerCase();
        const total = Number(i.total_amount ?? 0);
        const paidSoFar = Number(i.payments_total ?? 0);
        const stBalance = i.st_balance == null ? null : Number(i.st_balance);
        const left = status === "paid" ? 0 : stBalance != null ? stBalance : Math.max(0, total - paidSoFar);
        owed += left;
        const at = i.last_payment_at ?? i.st_paid_on ?? null;
        if (at && (!paidAt || at > paidAt)) paidAt = at;
    }
    return {
        none: false,
        draft: live.some((i) => (i.status ?? "").toLowerCase() === "draft"),
        paid: owed <= 0.004 && !live.some((i) => (i.status ?? "").toLowerCase() === "draft"),
        paidAt,
        owed,
    };
}

export function buildTrail(input: {
    jobId: string;
    job: Pick<JobDetail, "scheduled_start" | "arrived_at" | "completed_at" | "confirmed_at">;
    phase: Phase;
    facts: TrailFacts;
    /** Devices on the job and how many reached an outcome. */
    total: number;
    finished: number;
    couldnt: number;
    stillOpen: number;
    money: MoneyPicture;
    /** The Closeout topic that's the viewer's to do right now, if any. */
    mineTopic: BallTopic | null;
}): Stop[] {
    const { jobId, job, phase, facts, money } = input;
    const before = phase === "before";
    const stops: Stop[] = [];
    const link = (topic: BallTopic, label: string) =>
        input.mineTopic === topic ? { label, to: closeoutLink(jobId, topic) } : null;

    // Visit
    const visitDay = fmtDay(job.arrived_at ?? job.scheduled_start);
    stops.push(
        before
            ? job.scheduled_start
                ? { key: "visit", name: "Visit", state: "done", words: `Booked ${visitDay}`, action: null }
                : { key: "visit", name: "Visit", state: "mine", words: "Not booked yet", action: null }
            : { key: "visit", name: "Visit", state: "done", words: visitDay ?? "Visited", action: null },
    );

    // Completed
    if (before)
        stops.push({ key: "completed", name: "Completed", state: "later", words: "After the visit", action: null });
    else if (phase === "working")
        stops.push({
            key: "completed",
            name: "Completed",
            state: "waiting",
            words: "The tech is working",
            action: null,
        });
    else {
        const left = input.couldnt + input.stillOpen;
        const why = [
            input.couldnt > 0 ? `${input.couldnt} couldn't do` : null,
            input.stillOpen > 0 ? `${input.stillOpen} still open` : null,
        ].filter(Boolean);
        stops.push({
            key: "completed",
            name: "Completed",
            state: left > 0 ? "mine" : "done",
            words:
                input.total === 0
                    ? "Nothing on the job to count"
                    : [`${input.finished} of ${input.total}`, ...why].join(" · "),
            action: link("return_trip", "Book the return"),
        });
    }

    // City: its own stop only when the city files online (paper goes with the Customer send).
    if (facts.city.online) {
        const name = facts.city.name ?? "The city";
        if (before)
            stops.push({ key: "city", name: "City", state: "later", words: `${name} files online`, action: null });
        else if (facts.city.expected === 0)
            stops.push({ key: "city", name: "City", state: "later", words: "Nothing to file yet", action: null });
        else if (facts.city.filed >= facts.city.expected)
            stops.push({
                key: "city",
                name: "City",
                state: "done",
                words: `${facts.city.expected} filed`,
                action: null,
            });
        else
            stops.push({
                key: "city",
                name: "City",
                state: "mine",
                words: `${facts.city.filed} of ${facts.city.expected} filed · online`,
                action: link("filing", "Open city filing") ?? link("reports", "Open the reports"),
            });
    }

    // Customer
    const what = facts.city.online || !facts.city.name ? "Report + invoice" : "Report + city paper + invoice";
    if (facts.customer.lastSentAt)
        stops.push({
            key: "customer",
            name: "Customer",
            state: "done",
            words: `${facts.city.online || !facts.city.name ? "Sent" : "Report + city paper sent"} ${fmtDay(facts.customer.lastSentAt)}`,
            action: null,
        });
    else if (before) stops.push({ key: "customer", name: "Customer", state: "later", words: what, action: null });
    else if (facts.customer.packetHeld)
        stops.push({
            key: "customer",
            name: "Customer",
            state: "waiting",
            words: "Goes out when the city's reports are in",
            action: null,
        });
    else
        stops.push({
            key: "customer",
            name: "Customer",
            state: input.mineTopic === "packet" ? "mine" : "waiting",
            words: input.mineTopic === "packet" ? `${what}, ready to send` : `${what} · not sent yet`,
            action: link("packet", "Send"),
        });

    // Paid
    if (money.none)
        stops.push({
            key: "paid",
            name: "Paid",
            state: "later",
            words: before ? "After the visit" : "No invoice yet",
            action: link("invoice", "Make the invoice"),
        });
    else if (money.paid)
        stops.push({
            key: "paid",
            name: "Paid",
            state: "done",
            words: money.paidAt ? `Paid ${fmtDay(money.paidAt)}` : "Paid",
            action: null,
        });
    else
        stops.push({
            key: "paid",
            name: "Paid",
            state: input.mineTopic === "invoice" || input.mineTopic === "payment" ? "mine" : "waiting",
            words: money.draft
                ? `${money$(money.owed)} · invoice not finished`
                : facts.payment.netTerms
                  ? `${money$(money.owed)} · net terms`
                  : facts.payment.cardOnFile
                    ? `${money$(money.owed)} · card on file`
                    : `${money$(money.owed)} owed`,
            action: link("invoice", "Open the invoice") ?? link("payment", "Take the payment"),
        });
    return stops;
}
